import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["hamburger", "mobileMenu", "element"]
  static values  = { solid: Boolean }

  connect() {
    this.applySolidState()
    this.setupScrollListener()
    this.setupClickOutside()
    this.setupSmoothScroll()
    this.publishHeight()
  }

  disconnect() {
    if (this.heightObserver) this.heightObserver.disconnect()
  }

  // The toast stack is offset by `--app-header-h` so it can never cover the nav
  // or the user menu (#184). Nothing ever set that variable, so the calc fell
  // back to 4.5rem and produced a static 84px — correct at the 72px the header
  // measures at most widths, and 15px short at 1024px, where the nav wraps to
  // 99px. The toast sits BELOW the header in stacking order, so that strip was
  // not merely overlapped, it was invisible: the top edge of the message was
  // clipped at exactly the breakpoint the header changes shape.
  //
  // The header has no fixed height in CSS — it is `py-4` around content that
  // wraps — so only measurement can know it.
  publishHeight() {
    this.updateHeaderHeight()

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", () => this.updateHeaderHeight())
      return
    }

    this.heightObserver = new ResizeObserver(() => this.updateHeaderHeight())
    this.heightObserver.observe(this.element)
    if (this.hasMobileMenuTarget) this.heightObserver.observe(this.mobileMenuTarget)
    if (this.banner) this.heightObserver.observe(this.banner)
  }

  // The bar only, without the mobile menu. That menu is IN FLOW (max-height
  // 0 -> 500px), so including it would shove the toast most of a screen down
  // the moment somebody opens the menu. What the toast has to clear is the bar.
  updateHeaderHeight() {
    const full = this.element.getBoundingClientRect().height
    const menu = this.hasMobileMenuTarget ? this.mobileMenuTarget.getBoundingClientRect().height : 0
    const bar  = Math.max(0, Math.round(full - menu))

    document.documentElement.style.setProperty("--app-header-h", `${bar}px`)

    // The billing banner is a second fixed bar under the header; the toast has to clear it too.
    const banner = this.banner ? Math.round(this.banner.getBoundingClientRect().height) : 0
    document.documentElement.style.setProperty("--app-banner-h", `${banner}px`)
  }

  get banner() {
    return document.querySelector("[data-billing-banner]")
  }

  applySolidState() {
    if (this.solidValue) this.element.classList.add("solid")
  }

  setupScrollListener() {
    if (this.solidValue) return

    window.addEventListener("scroll", () => {
      this.element.classList.toggle("solid", window.scrollY > 50)
    })
  }

  setupClickOutside() {
    document.addEventListener("click", (e) => {
      if (!e.target.closest("nav") && this.mobileMenuTarget.classList.contains("active")) {
        this.closeMobileMenu()
      }
    })
  }

  setupSmoothScroll() {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener("click", (e) => {
        e.preventDefault()
        const target = document.querySelector(anchor.getAttribute("href"))
        if (target) target.scrollIntoView({ behavior: "smooth", block: "start" })
        if (this.mobileMenuTarget.classList.contains("active")) this.closeMobileMenu()
      })
    })
  }

  toggleMobileMenu() {
    this.hamburgerTarget.classList.toggle("active")
    this.mobileMenuTarget.classList.toggle("active")

    if (!this.solidValue && window.scrollY < 50) {
      this.element.classList.toggle("solid", this.mobileMenuTarget.classList.contains("active"))
    }
  }

  closeMobileMenu() {
    this.hamburgerTarget.classList.remove("active")
    this.mobileMenuTarget.classList.remove("active")

    if (!this.solidValue && window.scrollY < 50) {
      this.element.classList.remove("solid")
    }

    document.querySelectorAll(".mobile-dropdown-menu").forEach(d => d.classList.remove("active"))
  }

  toggleMobileDropdown(event) {
    const button     = event.currentTarget
    const dropdownId = button.dataset.dropdownId
    const dropdown   = document.getElementById(`${dropdownId}-dropdown`)
    const icon       = document.getElementById(`${dropdownId}-icon`)

    if (dropdown && icon) {
      dropdown.classList.toggle("active")
      icon.style.transform = dropdown.classList.contains("active") ? "rotate(180deg)" : "rotate(0deg)"
    }
  }
}
